import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { CloudStore, StoreProvider, setActiveStore } from "../lib/store.js";
import { Shell, Brand, Loading, ErrorText, Label, Message } from "./common.jsx";

export function AuthGate({ children }) {
  const [session, setSession] = useState(undefined);
  const [store, setStore] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") { location.href = "/reset"; return; }
      setSession(s || null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session && session.user ? session.user.id : null;

  useEffect(() => {
    if (!userId) { setStore(null); return; }
    let cancelled = false;
    let created = null;
    (async () => {
      setProblem(null);
      const { data, error } = await supabase.from("studio_members").select("studio_id, role, display_name, email, created_at").eq("user_id", userId).order("created_at", { ascending: true });
      if (cancelled) return;
      if (error) { setProblem({ kind: "error", message: error.message }); return; }
      if (!data || data.length === 0) { setProblem({ kind: "no-studio" }); return; }
      const m = data[0];
      created = new CloudStore(supabase, { studioId: m.studio_id, userId, role: m.role, displayName: m.display_name, email: m.email || session.user.email });
      try { await created.load(); } catch (e) { if (!cancelled) setProblem({ kind: "error", message: e.message || "Couldn't load your studio data." }); return; }
      if (cancelled) { created.destroy(); return; }
      setActiveStore(created);
      setStore(created);
    })();
    return () => { cancelled = true; if (created) created.destroy(); setActiveStore(null); };
  }, [userId]);

  useEffect(() => {
    if (!store) return;
    const warn = e => { if (store.dirty.size || store.inflight.size) { store.flushAll(); e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);

  if (session === undefined) return <Loading />;
  if (!session) return <Login />;
  if (problem && problem.kind === "no-studio") {
    return (
      <Message title="No studio access yet" body={"You're signed in as " + session.user.email + ", but this account isn't part of a studio. Ask the studio owner to send you an invite from Settings → Team, then open the link in that email."}>
        <button className="btn btn-secondary" style={{ alignSelf: "flex-start" }} onClick={() => supabase.auth.signOut()}>Sign out</button>
      </Message>
    );
  }
  if (problem) {
    return (
      <Message title="Something went wrong" body={problem.message}>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-primary" onClick={() => location.reload()}>Try again</button>
          <button className="btn btn-secondary" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </Message>
    );
  }
  if (!store) return <Loading label="Loading your studio…" />;
  return <StoreProvider value={store}>{children}</StoreProvider>;
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const signIn = async e => {
    e.preventDefault();
    setBusy(true); setErr(""); setNote("");
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setErr(/invalid/i.test(error.message) ? "That email and password don't match. Try again, or use “Forgot password”." : error.message);
  };
  const magic = async () => {
    if (!email.includes("@")) { setErr("Enter your email first."); return; }
    setBusy(true); setErr(""); setNote("");
    const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: false, emailRedirectTo: location.origin + "/" } });
    setBusy(false);
    if (error) setErr(error.message); else setNote("Check your email for a sign-in link.");
  };
  const forgot = async () => {
    if (!email.includes("@")) { setErr("Enter your email first."); return; }
    setBusy(true); setErr(""); setNote("");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: location.origin + "/reset" });
    setBusy(false);
    if (error) setErr(error.message); else setNote("If that email has an account, a reset link is on its way.");
  };

  return (
    <Shell>
      <div>
        <Brand sub="Planner dashboard" />
        <h1 className="display" style={{ fontSize: 40, margin: "10px 0 0" }}>Welcome back.</h1>
        <p className="display-italic" style={{ fontSize: 20, color: "var(--graphite)", marginTop: 4 }}>Pick up where the story left off.</p>
      </div>
      <form onSubmit={signIn} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Label text="Email"><input className="input" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required /></Label>
        <Label text="Password"><input className="input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required /></Label>
        <button className="btn btn-accent btn-block" style={{ marginTop: 6 }} disabled={busy} type="submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <ErrorText>{err}</ErrorText>
      {note && <div role="status" className="error-text">{note}</div>}
      <hr className="rule" />
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", justifyContent: "space-between" }}>
        <button className="btn-link" disabled={busy} onClick={magic}>Email me a sign-in link</button>
        <button className="btn-link" disabled={busy} onClick={forgot}>Forgot password</button>
      </div>
    </Shell>
  );
}
